// Motor de las dos tablas de líneas.
//
// No sabe qué columnas pinta: las recibe. Así los dos grupos —pendiente y
// facturado— pueden tener juegos distintos sin duplicar el andamiaje, y el pie
// se deriva de las MISMAS columnas visibles, que es lo que impide que un total
// acabe bajo la columna equivocada.
//
// Se renderizan TODAS las líneas del grupo, sin paginar, dentro de un
// contenedor de alto medido: cabecera pegada arriba, totales pegados abajo y
// scroll propio en medio. Es el comportamiento de una rejilla de escritorio y
// arregla lo que la paginación hacía mal: con páginas había que llegar al final
// para ver los totales, y ahora se leen desde la línea 1 y desde la 52.
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SubTotalsRow } from '@/components/common/SubTotalsRow'
import { CabeceraMarca, FilaMarcable, MarcaCelda } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { SortArrow } from '@/components/common/SortableHeader'
import type { ColumnaPie } from '@/utils/subtotalesLineas'
import type { PedidoVentaLinea } from '@/types'
import { filaResaltada } from '../formato'
import { bordeTenueCls, tdCls, thCls } from './estilos'
import { useAltoDisponible } from './useAltoDisponible'
import type { ColumnaLinea } from './celdasLinea'
import type { MarcasFilas } from '@/types'

export interface OrdenTabla {
  key: string
  dir: 'asc' | 'desc'
}

interface Props {
  dark: boolean
  lineas: PedidoVentaLinea[]
  columnas: ColumnaLinea[]
  /** Filas que habría sin filtrar, para que el pie diga «4 de 52». */
  totalSinFiltrar?: number
  orden: OrdenTabla | null
  onOrden: (key: string) => void
  /** Resalta las filas que casan con la búsqueda. */
  coincide?: ((articulo?: string, descripcion?: string) => boolean) | null
  /** Marcas del usuario. Vienen de la página porque su ámbito es el pedido
   *  entero: los dos grupos de líneas comparten almacén y una línea marcada en
   *  «pendientes» sigue marcada si reaparece en «facturadas». */
  marcas: MarcasFilas
}

/** Ordena las líneas por una columna.
 *
 *  Vive fuera del componente para que la vista pueda ordenar antes de decidir
 *  qué hace con el resultado. */
export function ordenarLineas(
  lineas: PedidoVentaLinea[],
  columnas: ColumnaLinea[],
  orden: OrdenTabla | null,
): PedidoVentaLinea[] {
  if (!orden) return lineas
  const col = columnas.find(c => c.key === orden.key)
  if (!col?.valorOrden) return lineas
  const dir = orden.dir === 'asc' ? 1 : -1
  return [...lineas].sort((a, b) => {
    const av = col.valorOrden!(a)
    const bv = col.valorOrden!(b)
    // Los huecos siempre al final, mande el sentido que mande: un hueco no es
    // «lo más pequeño», es «no hay dato».
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv)
    return dir * String(av).localeCompare(String(bv), 'es-ES')
  })
}

export function TablaLineas({
  dark, lineas, columnas, totalSinFiltrar, orden, onOrden, coincide, marcas,
}: Props) {
  const { t } = useTranslation()
  const caja = useRef<HTMLDivElement>(null)
  const alto = useAltoDisponible(caja)

  const columnasPie: ColumnaPie<PedidoVentaLinea>[] = [
    // Hueco del canalón de marcas: sin él el pie se desplaza una columna.
    { key: '__marca__' },
    ...columnas.map(c => ({
      key: c.key,
      agg: c.agg,
      format: c.formatoTotal,
    })),
  ]

  const th = thCls(dark)
  const td = tdCls(dark)
  const divisor = bordeTenueCls(dark)
  // Fondos OPACOS en cabecera y pie: al quedarse pegados, cualquier
  // transparencia dejaría verse las filas pasando por debajo del texto.
  const fondoTh = dark ? 'bg-[#0d141f]' : 'bg-white'
  // Un punto más claro que la cabecera: el pie sigue leyéndose como banda de
  // totales sin necesitar un borde grueso ni un color propio.
  const fondoPie = dark ? '[&_td]:!bg-[#141d2b]' : '[&_td]:!bg-slate-100'
  const marco = dark ? 'border-white/8' : 'border-slate-200'
  const filaHover = dark ? 'hover:bg-white/[0.035]' : 'hover:bg-slate-50/80'

  return (
    <div
      ref={caja}
      style={{ maxHeight: alto }}
      className={`overflow-auto scroll-thin rounded-lg border ${marco}`}
    >
      <table className="w-full min-w-max table-fixed border-collapse text-left">
        <colgroup>
          <col className="w-6" />
          {columnas.map(c => <col key={c.key} className={c.ancho} />)}
        </colgroup>

        {/* `sticky` va en las CELDAS y no en <thead>/<tfoot>: sobre el elemento
            de sección los navegadores no lo aplican de forma fiable. */}
        <thead>
          <tr>
            <CabeceraMarca className={`${fondoTh} sticky top-0 z-10 border-b ${divisor}`} />
            {columnas.map(c => {
              const activa = orden?.key === c.key
              const ordenable = !!c.valorOrden
              return (
                <th
                  key={c.key}
                  className={`${th} ${fondoTh} sticky top-0 z-10 border-b ${divisor} ${
                    c.alinear === 'right' ? 'text-right' : ''
                  }`}
                  aria-sort={activa ? (orden!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {ordenable ? (
                    <button
                      type="button"
                      onClick={() => onOrden(c.key)}
                      className={`inline-flex items-center gap-1 cursor-pointer transition-colors duration-150 hover:text-sky-500 ${
                        activa ? 'text-sky-500' : ''
                      } ${c.alinear === 'right' ? 'flex-row-reverse' : ''}`}
                    >
                      {c.etiqueta}
                      <SortArrow active={activa} dir={orden?.dir ?? 'asc'} dark={dark} />
                    </button>
                  ) : c.etiqueta}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {lineas.map((l, i) => {
            // La clave de la marca sale del NEGOCIO, no del índice: si saliera
            // del índice, ordenar por otra columna movería las marcas de fila.
            // `inventtransid` + factura identifica la fila dentro del pedido,
            // que es el ámbito del almacén.
            const clave = claveFila(l.inventtransid ?? l.numLinea ?? '', l.factura ?? '')
            return (
            <FilaMarcable
              marcas={marcas}
              clave={clave}
              dark={dark}
              key={`${l.inventtransid ?? l.numLinea ?? ''}-${l.factura ?? ''}-${i}`}
              className={`border-t ${divisor} transition-colors duration-150 ${filaHover} ${
                coincide?.(l.articulo, l.descripcion) ? filaResaltada(dark) : ''
              }`}
            >
              <MarcaCelda marcas={marcas} clave={clave} dark={dark} densa />
              {columnas.map(c => (
                <td
                  key={c.key}
                  className={`${td} ${c.alinear === 'right' ? 'text-right tabular-nums' : ''}`}
                >
                  {c.render(l)}
                </td>
              ))}
            </FilaMarcable>
            )
          })}
        </tbody>

        {/* Totales siempre visibles: es la cifra que se contrasta con el resumen,
            y tener que bajar 52 filas para verla era el defecto de la paginación.
            El fondo se fuerza OPACO: `SubTotalsRow` lo trae translúcido —pensado
            para una fila suelta dentro de un desplegable— y al quedarse pegado
            dejaba verse la fila que pasa por debajo. */}
        <tfoot className={`[&_td]:sticky [&_td]:bottom-0 [&_td]:z-10 ${fondoPie}`}>
          <SubTotalsRow
            dark={dark}
            rows={lineas}
            columnas={columnasPie}
            totalSinFiltrar={totalSinFiltrar}
          />
        </tfoot>
      </table>

      {lineas.length === 0 && (
        <p className={`px-3 py-8 text-center text-[12px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('common.noResults')}
        </p>
      )}
    </div>
  )
}
